import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import CurrentPlanButton from 'pricing-pages-lib/buttons/CurrentPlanButton';
import { getButtonVariantProps, getTrellisButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { useSelector } from 'react-redux';
import { HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import { FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { getProductLevelFromApiNameString, getProductLineFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { ApiNameToProductTranslationKeyMap } from '../../../constants/ApiNameToProductTranslationKeyMap';
import { ProductLevelTranslationKeyMap } from '../../../constants/ProductLevelToTranslationKeyMap';
import { SCREENS } from '../../../constants/tracking/Screens';
import { PRODUCT_PAGE_FEATURE_TABLE_HEADER } from '../../../constants/tracking/TrackingSources';
import HubProductCTA from '../../../features/purchaseMotions/components/HubProductCTA';
import MerchCardCTAOverridesWrapper from '../../../features/purchaseMotions/components/MerchCardCTAOverridesWrapper';
import { TalkToSalesFallback } from '../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useHubPurchaseMotions } from '../../../features/purchaseMotions/hooks/useHubPurchaseMotions';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { ownsCrmSuiteProductLevel } from '../../../selectors/ProductInventorySelectors';
import { productSelector } from '../../../selectors/ProductsSelectors';
import { useFTS0012Experiment } from '../../../utils/experiments/fts0012';
import SignupButton from '../../common/SignupButton';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import ErrorBoundary from '../../error-boundary/ErrorBoundary';
import { useSkuAdditionalUnits } from '../../merch-card/useSkuAdditionalUnits';
import FeatureTableColTag from '../FeatureTableColTag';
import FeatureTableFreePriceDisplay from '../FeatureTableFreePriceDisplay';
import FeatureTablePriceDisplay from '../FeatureTablePriceDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ProductFeatureTableHeaderCellContent = ({
  productLevel,
  isLoading,
  isFreeUser,
  skuId,
  productLine,
  apiName
}) => {
  const {
    additionalUnitSkuId,
    isAdditionalUnitSkuIdAQuantityPack,
    additionalUnitQuantity
  } = useSkuAdditionalUnits({
    productLevel,
    productLine
  });
  const purchaseMotions = useHubPurchaseMotions({
    skuId
  });
  const ctaVariantProps = getButtonVariantProps({
    variant: 'table-header'
  });
  const trellisCtaVariantProps = getTrellisButtonVariantProps({
    variant: 'table-header'
  });
  const signupCtaKey = 'pricingPage.signupCtas.getStartedFreeConcise';
  if (productLevel !== 'FREE' && skuId && apiName) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        marginBlock: "100",
        as: "div",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: isLoading,
          heights: [20],
          widths: [140],
          children: /*#__PURE__*/_jsx(FeatureTablePriceDisplay, {
            apiName: apiName
          })
        })
      }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isLoading,
        heights: [40],
        widths: [140],
        children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
          children: /*#__PURE__*/_jsx(ErrorBoundary, {
            fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
              apiName: apiName,
              trackingSource: PRODUCT_PAGE_FEATURE_TABLE_HEADER,
              screen: SCREENS.PRODUCTS_PLANS
            }),
            children: /*#__PURE__*/_jsx(MerchCardCTAOverridesWrapper, Object.assign({
              skuId: skuId,
              trackingSource: PRODUCT_PAGE_FEATURE_TABLE_HEADER
            }, ctaVariantProps, {
              children: /*#__PURE__*/_jsx(HubProductCTA, Object.assign({
                purchaseMotion: purchaseMotions[0],
                skuId: skuId,
                productLine: productLine,
                additionalUnitSkuId: additionalUnitSkuId,
                additionalUnitQuantity: isAdditionalUnitSkuIdAQuantityPack ? additionalUnitQuantity : 0,
                trackingSource: PRODUCT_PAGE_FEATURE_TABLE_HEADER,
                purchaseMotionLocation: 'GENERIC' // replace with FEATURE_TABLE when available https://git.hubteam.com/HubSpot/pricing-pages-unified-ui/issues/11974
              }, ctaVariantProps))
            }))
          })
        }))
      })]
    });
  }
  if (isFreeUser) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(View, {
        marginBlock: "100",
        as: "div",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: isLoading,
          heights: [20],
          widths: [140],
          children: /*#__PURE__*/_jsx(FeatureTableFreePriceDisplay, {})
        })
      }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isLoading,
        heights: [40],
        widths: [140],
        children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
          children: /*#__PURE__*/_jsx(ErrorBoundary, {
            fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
              apiName: apiName,
              trackingSource: PRODUCT_PAGE_FEATURE_TABLE_HEADER,
              screen: SCREENS.PRODUCTS_PLANS
            }),
            children: /*#__PURE__*/_jsx(CurrentPlanButton, {
              visible: trellisCtaVariantProps.visible
            })
          })
        }))
      })]
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      marginBlock: "100",
      as: "div",
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isLoading,
        heights: [20],
        widths: [140],
        children: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          alignItems: "center",
          children: /*#__PURE__*/_jsx(FeatureTableFreePriceDisplay, {})
        })
      })
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: isLoading,
      heights: [40],
      widths: [140],
      children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
            apiName: apiName,
            trackingSource: PRODUCT_PAGE_FEATURE_TABLE_HEADER,
            screen: SCREENS.PRODUCTS_PLANS
          }),
          children: /*#__PURE__*/_jsx(SignupButton, Object.assign({
            sourceId: "pricing-hero",
            productLine: "crm",
            use: "primary",
            trackingSource: PRODUCT_PAGE_FEATURE_TABLE_HEADER,
            ctaKey: signupCtaKey
          }, ctaVariantProps))
        })
      }))
    })]
  });
};
const ProductFeatureTableHeaderCell = ({
  apiName,
  showRecommended = false,
  recommendedProductLevel = PROFESSIONAL
}) => {
  const isAppReady = useGlobalLoading();
  const isFreeUser = useSelector(state => isAppReady && ownsCrmSuiteProductLevel(state, {
    productLevel: 'FREE'
  }));
  const product = useSelector(state => {
    if (!isAppReady) return null;
    return productSelector(state, {
      apiName
    });
  });
  const productLevel = (product === null || product === void 0 ? void 0 : product.productLevel) || getProductLevelFromApiNameString(apiName);
  const productLine = (product === null || product === void 0 ? void 0 : product.productLine) || getProductLineFromApiNameString(apiName);
  const skuIdToUse = product === null || product === void 0 ? void 0 : product.currentSkuId;
  const {
    isFTS0012Variant
  } = useFTS0012Experiment();
  const apiNameForPrice = productLevel === FREE ? HUBSPOT_FREE : apiName;
  if (!productLevel || !productLine) return null;
  const fts12Title = productLevel === STARTER ? 'products.crm.STARTER' : ProductLevelTranslationKeyMap[productLevel];
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    paddingInline: "0",
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(FeatureTableColTag, {
      apiName: apiName,
      recommendedProductLevel: recommendedProductLevel,
      showRecommended: showRecommended
    }), /*#__PURE__*/_jsx(View, {
      maxInlineSize: "24ch",
      children: /*#__PURE__*/_jsx(Text, {
        as: "strong",
        typography: "heading-50",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: isFTS0012Variant ? fts12Title : ApiNameToProductTranslationKeyMap[apiName]
        })
      })
    }), /*#__PURE__*/_jsx(ProductFeatureTableHeaderCellContent, {
      productLevel: productLevel,
      isLoading: !isAppReady,
      isFreeUser: isFreeUser,
      skuId: skuIdToUse,
      productLine: productLine,
      apiName: apiNameForPrice
    })]
  });
};
export default ProductFeatureTableHeaderCell;